import React, { useState, useEffect, useLayoutEffect, Fragment, useRef, useMemo } from 'react';
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    Transition,
    TransitionChild,
} from '@headlessui/react';
import { Button } from 'components/ui';
import { Input } from 'components/ui/Form';
import { FilterDropdown } from 'components/leads/filters';
import { XMarkIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Calendar } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { generateOfferLetter, getInternationalStudentOfferLetterDetails, getInternationalStudentMasterFieldsWithOptions } from 'services/offerLetterService';
import { getMasterColleges, getMasterCoursesByCollegeId, getMasterCourseDetailsByCourseCode } from 'services/integrationService';
import { generateOfferLetterForInternationalStudent, getInternationalOfferLetterByLeadId } from 'services/leadsService';
import { toast } from 'sonner';
import { convertToUTC } from 'utils/dateUtils';
import { jsPDF } from 'jspdf';
import html2canvasPro from 'html2canvas-pro';
import { OfferLetterPDFTemplate } from './OfferLetterPDFTemplate';

/**
 * PopoverDatePicker Component using react-date-range Calendar
 * Renders a single date picker styled as a form input, matching FilterDropdown layout
 */
const PopoverDatePicker = ({
    value,
    onChange,
    label,
    placeholder = "Select Date",
    disabled = false,
    className = "",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isOpen) return;
            const isInsideTrigger = dropdownRef.current?.contains(event.target);
            const isInsideMenu = menuRef.current?.contains(event.target);
            if (!isInsideTrigger && !isInsideMenu) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const [menuStyle, setMenuStyle] = useState({});

    useLayoutEffect(() => {
        if (isOpen && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            setMenuStyle({
                position: 'fixed',
                top: rect.top - 4,
                left: rect.left,
                transform: 'translateY(-100%)',
                zIndex: 9999,
            });
        }
    }, [isOpen]);

    const displayValue = () => {
        if (!value) return placeholder;
        return dayjs(value).format('MMM DD, YYYY');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange(null);
    };

    return (
        <div className={clsx("relative w-full", className)} ref={dropdownRef}>
            {label && (
                <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    {label}
                </label>
            )}

            <div className="relative">
                <Button
                    type="button"
                    variant="outlined"
                    disabled={disabled}
                    className={clsx(
                        "flex w-full items-center justify-between gap-2 border-gray-300 bg-white px-3 py-2 text-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 normal-case font-normal text-left",
                        disabled && "cursor-not-allowed opacity-60 bg-gray-50 dark:bg-dark-900"
                    )}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                >
                    <span className={value ? "text-gray-900 dark:text-gray-100 font-medium" : "text-gray-500 dark:text-gray-400"}>
                        {displayValue()}
                    </span>
                    <div className="flex items-center gap-1">
                        {value && !disabled && (
                            <XCircleIcon
                                className="size-4 text-gray-400 transition-colors hover:text-red-500"
                                onClick={handleClear}
                            />
                        )}
                        <ChevronDownIcon
                            className={clsx(
                                "size-4 transition-transform text-gray-450",
                                isOpen && "rotate-180"
                            )}
                        />
                    </div>
                </Button>

                {isOpen && createPortal(
                    <div
                        ref={menuRef}
                        className="rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800 p-2 z-[9999] overflow-hidden"
                        style={menuStyle}
                    >
                        <Calendar
                            date={value ? new Date(value) : new Date()}
                            onChange={(date) => {
                                onChange(dayjs(date).format('YYYY-MM-DD'));
                                setIsOpen(false);
                            }}
                            color="#3b82f6"
                            showDateDisplay={false}
                        />
                    </div>,
                    document.body
                )}
            </div>
        </div>
    );
};

/**
 * GenerateOfferLetterModal Component
 * Form modal for generating a new offer letter
 */
const GenerateOfferLetterModal = ({
    isOpen,
    onClose,
    leadId,
    leadData,
    progressData,
    onSuccess = () => { },
    mode = 'create',
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [collegesList, setCollegesList] = useState([]);
    const [coursesList, setCoursesList] = useState([]);
    const [isLoadingCourses, setIsLoadingCourses] = useState(false);
    const [isFetchingCourseDetails, setIsFetchingCourseDetails] = useState(false);
    const [apiData, setApiData] = useState(null);
    const [masterFields, setMasterFields] = useState([]);
    const [intLeadsOfferLetterId, setIntLeadsOfferLetterId] = useState(null);
    const templateRef = useRef(null);
    const [formData, setFormData] = useState({
        applicantName: leadData?.name || '',
        applicantGender: '',
        fatherGuardianName: '',
        dateOfBirth: '',
        applicantCountry: '',
        applicantPassportNo: '',
        applicantSiiId: '',
        applicantEmail: leadData?.email || '',
        fatherEmail: '',
        fatherMobileNumber: '',
        guardianEmail: '',
        guardianMobileNumber: '',
        collegeId: '',
        courseCode: '',
        programName: '',
        durationOfProgram: '',
        startDate: '',
        expectedEndDate: '',
        semesters: '',
        annualCourseFee: '',
        scholarship: '',
        feeAfterScholarship: '',
        provisionalRegistrationFeeDate: '',
        bankName: '',
        bankBranch: '',
        bankAccountName: '',
        bankAccountNumber: '',
        bankSwiftCode: '',
        bankIfscCode: '',
        bankMicrCode: '',
    });

    useEffect(() => {
        const fetchDetails = async () => {
            if (!isOpen) return;

            // Fetch master fields with options first
            let parsedFields = [];
            try {
                const fieldsResponse = await getInternationalStudentMasterFieldsWithOptions();
                if (fieldsResponse && (fieldsResponse.success || fieldsResponse.statusCode === 200)) {
                    const rawFields = fieldsResponse.data;
                    const parsed = typeof rawFields === 'string' ? JSON.parse(rawFields) : rawFields;
                    if (Array.isArray(parsed)) {
                        parsedFields = parsed;
                        setMasterFields(parsed);
                    }
                }
            } catch (fErr) {
                console.error("Error fetching master fields:", fErr);
            }

            // Fetch colleges list
            try {
                const collegesRes = await getMasterColleges();
                if (collegesRes && collegesRes.success && Array.isArray(collegesRes.data)) {
                    setCollegesList(collegesRes.data);
                }
            } catch (cErr) {
                console.error("Error fetching colleges:", cErr);
            }

            if (mode === 'edit') {
                try {
                    const response = await getInternationalOfferLetterByLeadId(leadId);
                    if (response && (response.success || response.statusCode === 200)) {
                        const apiDataObj = response.data;
                        
                        let rawJsonObj = {};
                        if (apiDataObj?.RawJson) {
                            rawJsonObj = typeof apiDataObj.RawJson === 'string' ? JSON.parse(apiDataObj.RawJson) : apiDataObj.RawJson;
                        }

                        const getFieldValueFromFields = (fields, fieldNameOrLabel) => {
                            if (!Array.isArray(fields)) return null;
                            const lower = fieldNameOrLabel.trim().toLowerCase();
                            const found = fields.find(f => 
                                f.FieldName?.trim().toLowerCase() === lower || 
                                f.FieldLabel?.trim().toLowerCase() === lower
                            );
                            return found ? found.Value : null;
                        };

                        const fields = apiDataObj?.OfferLetterFields || [];
                        const fName = getFieldValueFromFields(fields, 'first name') || getFieldValueFromFields(fields, 'first_name') || '';
                        const lName = getFieldValueFromFields(fields, 'last name') || getFieldValueFromFields(fields, 'last_name') || '';
                        const mergedName = [fName, lName].filter(Boolean).join(' ');

                        const initialData = {
                            applicantName: mergedName || leadData?.name || '',
                            applicantGender: getFieldValueFromFields(fields, 'gender') || '',
                            fatherGuardianName: getFieldValueFromFields(fields, "father's name") || getFieldValueFromFields(fields, "guardian's name") || '',
                            dateOfBirth: getFieldValueFromFields(fields, 'date_of_birth') || '',
                            applicantCountry: getFieldValueFromFields(fields, 'country') || '',
                            applicantPassportNo: getFieldValueFromFields(fields, 'passport no.') || '',
                            applicantSiiId: getFieldValueFromFields(fields, ' SII ID') || getFieldValueFromFields(fields, 'SII ID') || '',
                            applicantEmail: getFieldValueFromFields(fields, 'email') || leadData?.email || '',
                            fatherEmail: getFieldValueFromFields(fields, 'email address') || '',
                            fatherMobileNumber: getFieldValueFromFields(fields, "father's contact number") || '',
                            guardianEmail: '',
                            guardianMobileNumber: getFieldValueFromFields(fields, 'emergency contact details') || '',
                            
                            collegeId: rawJsonObj.collegeId || '',
                            courseCode: rawJsonObj.courseCode || '',
                            programName: rawJsonObj.programName || '',
                            durationOfProgram: rawJsonObj.durationOfProgram || '',
                            startDate: rawJsonObj.startDate || '',
                            expectedEndDate: rawJsonObj.expectedEndDate || '',
                            semesters: rawJsonObj.semesters || '',
                            annualCourseFee: rawJsonObj.annualCourseFee || '',
                            scholarship: rawJsonObj.ScholarshipPercentage || '',
                            feeAfterScholarship: rawJsonObj.feeAfterScholarship || '',
                            provisionalRegistrationFeeDate: rawJsonObj.provisionalRegistrationFeeDate || '',
                            bankName: rawJsonObj.bankName || '',
                            bankBranch: rawJsonObj.bankBranch || '',
                            bankAccountName: rawJsonObj.bankAccountName || '',
                            bankAccountNumber: rawJsonObj.bankAccountNumber || '',
                            bankSwiftCode: rawJsonObj.bankSwiftCode || '',
                            bankIfscCode: rawJsonObj.bankIfscCode || '',
                            bankMicrCode: rawJsonObj.bankMicrCode || '',
                        };

                        if (Array.isArray(parsedFields)) {
                            parsedFields.forEach((field) => {
                                const val = getFieldValueFromFields(fields, field.FieldName) || '';
                                initialData[field.FieldName] = val;
                            });
                        }

                        // Also clean up initialData standard fields if they are "N/A" or "NA"
                        Object.keys(initialData).forEach(key => {
                            const val = initialData[key];
                            if (val && typeof val === 'string' && (val.trim().toLowerCase() === 'n/a' || val.trim().toLowerCase() === 'na')) {
                                initialData[key] = '';
                            }
                        });

                        if (initialData.collegeId) {
                            try {
                                const coursesRes = await getMasterCoursesByCollegeId(initialData.collegeId);
                                if (coursesRes && coursesRes.success && Array.isArray(coursesRes.data)) {
                                    setCoursesList(coursesRes.data);
                                }
                            } catch (err) {
                                console.error("Error fetching courses by college ID in edit mode:", err);
                            }
                        }

                        setFormData(initialData);
                        setApiData(apiDataObj);
                        if (apiDataObj?.IntLeadsOfferLetterId) {
                            setIntLeadsOfferLetterId(apiDataObj.IntLeadsOfferLetterId);
                        } else if (apiDataObj?.Id) {
                            setIntLeadsOfferLetterId(apiDataObj.Id);
                        }
                    }
                } catch (error) {
                    console.error("Error loading saved international offer letter:", error);
                }
            } else {
                const applicantId = progressData?.ApplicationId || leadData?.ApplicationId || leadData?.BasicInformation?.ApplicationId || leadId;
                if (!applicantId) return;
                
                try {
                    // Silently fetch details to patch the modal.
                    // Do NOT show any loader, success, or error toasts here to comply with CRM experience guidelines.
                    const response = await getInternationalStudentOfferLetterDetails(applicantId);
                    if (response && (response.success || response.statusCode === 200)) {
                        const rawData = response.data;
                        let apiDataObj = {};
                        if (rawData) {
                            apiDataObj = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
                        }
                        
                        // Initialize with standard fields
                        const initialData = {
                            applicantName: apiDataObj.FullName || leadData?.name || '',
                            applicantGender: apiDataObj.Gender || '',
                            fatherGuardianName: apiDataObj.FathersName || apiDataObj.GuardiansName || '',
                            dateOfBirth: apiDataObj.DateOfBirth ? apiDataObj.DateOfBirth.split('T')[0] : '',
                            applicantCountry: apiDataObj.Country || '',
                            applicantPassportNo: apiDataObj.PassportNo || '',
                            applicantSiiId: apiDataObj.SIIIID || apiDataObj.SIIID || apiDataObj.SiiiId || apiDataObj.SiiId || apiDataObj.Siiid || '',
                            applicantEmail: apiDataObj.StudentEmail || leadData?.email || '',
                            fatherEmail: apiDataObj.FathersEmail || '',
                            fatherMobileNumber: apiDataObj.FathersPhone || '',
                            guardianEmail: apiDataObj.GuardiansEmail || '',
                            guardianMobileNumber: apiDataObj.GuardiansPhoneNumber || '',
                            collegeId: '',
                            courseCode: '',
                            programName: '',
                            durationOfProgram: '',
                            startDate: '',
                            expectedEndDate: '',
                            semesters: '',
                            annualCourseFee: '',
                            scholarship: '',
                            feeAfterScholarship: '',
                        };

                        // Map master fields dynamically
                        if (Array.isArray(parsedFields)) {
                            parsedFields.forEach((field) => {
                                const fieldName = field.FieldName;
                                const trimmed = fieldName.trim();
                                const lower = trimmed.toLowerCase();
                                
                                let value = '';
                                switch (lower) {
                                    case 'sii id':
                                        value = apiDataObj.SIIIID || apiDataObj.SIIID || apiDataObj.SiiiId || apiDataObj.SiiId || apiDataObj.Siiid || '';
                                        break;
                                    case 'emergency contact details':
                                        value = apiDataObj.EmergencyContactDetails || apiDataObj.GuardiansPhoneNumber || apiDataObj.FathersPhone || '';
                                        break;
                                    case 'country':
                                        value = apiDataObj.Country || leadData?.country || '';
                                        break;
                                    case 'first name':
                                    case 'first_name':
                                        value = apiDataObj.FirstName || leadData?.firstName || (apiDataObj.FullName ? apiDataObj.FullName.split(' ')[0] : (leadData?.name ? leadData.name.split(' ')[0] : ''));
                                        break;
                                    case 'last name':
                                    case 'last_name':
                                        value = apiDataObj.LastName || leadData?.lastName || (apiDataObj.FullName ? apiDataObj.FullName.split(' ').slice(1).join(' ') : (leadData?.name ? leadData.name.split(' ').slice(1).join(' ') : ''));
                                        break;
                                    case 'father\'s contact number':
                                        value = apiDataObj.FathersPhone || '';
                                        break;
                                    case 'gender':
                                        value = apiDataObj.Gender || leadData?.gender || '';
                                        break;
                                    case 'date_of_birth':
                                        if (apiDataObj.DateOfBirth) {
                                            value = apiDataObj.DateOfBirth.split('T')[0];
                                        } else if (leadData?.dob) {
                                            try {
                                                value = new Date(leadData.dob).toISOString().split('T')[0];
                                            } catch {
                                                value = '';
                                            }
                                        }
                                        break;
                                    case 'guardian\'s name':
                                        value = apiDataObj.GuardiansName || '';
                                        break;
                                    case 'father\'s name':
                                        value = apiDataObj.FathersName || leadData?.FatherName || '';
                                        break;
                                    case 'email address':
                                        value = apiDataObj.FathersEmail || apiDataObj.GuardiansEmail || '';
                                        break;
                                    case 'passport no.':
                                        value = apiDataObj.PassportNo || '';
                                        break;
                                    case 'email':
                                        value = apiDataObj.StudentEmail || leadData?.email || '';
                                        break;
                                    default: {
                                        const exactMatch = Object.keys(apiDataObj).find(k => k.toLowerCase() === lower);
                                        if (exactMatch) {
                                            value = apiDataObj[exactMatch] || '';
                                        }
                                        break;
                                    }
                                }
                                
                                // Normalize "N/A" or "NA" values to empty string to show proper placeholder
                                if (value && typeof value === 'string' && (value.trim().toLowerCase() === 'n/a' || value.trim().toLowerCase() === 'na')) {
                                    value = '';
                                }
                                initialData[fieldName] = value;
                            });

                            // Also clean up initialData standard fields if they are "N/A" or "NA"
                            Object.keys(initialData).forEach(key => {
                                const val = initialData[key];
                                if (val && typeof val === 'string' && (val.trim().toLowerCase() === 'n/a' || val.trim().toLowerCase() === 'na')) {
                                    initialData[key] = '';
                                }
                            });
                        }

                        setFormData(initialData);
                        setApiData(apiDataObj);
                        // If this lead already has a saved offer letter, track its ID for edit mode
                        if (apiDataObj?.Id) {
                            setIntLeadsOfferLetterId(apiDataObj.Id);
                        }
                    }
                } catch (error) {
                    console.error("Error loading applicant details:", error);
                }
            }
        };

        fetchDetails();
    }, [isOpen, leadId, leadData, progressData]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => {
            const next = { ...prev, [field]: value };
            
            // Bidirectional sync for dynamic fields to standard fields
            const trimmed = field.trim();
            const lower = trimmed.toLowerCase();
            
            if (lower === 'sii id') {
                next.applicantSiiId = value;
            } else if (lower === 'country') {
                next.applicantCountry = value;
            } else if (lower === 'gender') {
                next.applicantGender = value;
            } else if (lower === 'date_of_birth') {
                next.dateOfBirth = value;
            } else if (lower === 'guardian\'s name') {
                if (!next.fatherGuardianName) {
                    next.fatherGuardianName = value;
                }
            } else if (lower === 'father\'s name') {
                next.fatherGuardianName = value;
            } else if (lower === 'passport no.') {
                next.applicantPassportNo = value;
            } else if (lower === 'email') {
                next.applicantEmail = value;
            } else if (lower === 'email address') {
                next.fatherEmail = value;
            } else if (lower === 'father\'s contact number') {
                next.fatherMobileNumber = value;
            } else if (lower === 'emergency contact details') {
                next.guardianMobileNumber = value;
            }
            
            return next;
        });
    };

    const handleCollegeChange = async (collegeId) => {
        setFormData(prev => {
            const next = {
                ...prev,
                collegeId: collegeId,
                courseCode: '',
                programName: '',
                durationOfProgram: '',
                startDate: '',
                expectedEndDate: '',
                semesters: '',
                annualCourseFee: '',
                scholarship: '',
                feeAfterScholarship: '',
                bankName: '',
                bankBranch: '',
                bankAccountName: '',
                bankAccountNumber: '',
                bankSwiftCode: '',
                bankIfscCode: '',
                bankMicrCode: '',
                applicantSiiId: '',
            };
            const siiKey = Object.keys(prev).find(k => k.trim().toLowerCase() === 'sii id');
            if (siiKey) {
                next[siiKey] = '';
            } else {
                next['SII ID'] = '';
                next['sii id'] = '';
            }
            return next;
        });
        setCoursesList([]);

        if (!collegeId) return;

        setIsLoadingCourses(true);
        try {
            const coursesRes = await getMasterCoursesByCollegeId(collegeId);
            if (coursesRes && coursesRes.success && Array.isArray(coursesRes.data)) {
                setCoursesList(coursesRes.data);
            }
        } catch (err) {
            console.error("Error fetching courses by college ID:", err);
        } finally {
            setIsLoadingCourses(false);
        }
    };

    const handleCourseChange = async (courseCode) => {
        if (!courseCode) {
            setFormData(prev => {
                const next = {
                    ...prev,
                    courseCode: '',
                    programName: '',
                    durationOfProgram: '',
                    startDate: '',
                    expectedEndDate: '',
                    semesters: '',
                    annualCourseFee: '',
                    scholarship: '',
                    feeAfterScholarship: '',
                    bankName: '',
                    bankBranch: '',
                    bankAccountName: '',
                    bankAccountNumber: '',
                    bankSwiftCode: '',
                    bankIfscCode: '',
                    bankMicrCode: '',
                    applicantSiiId: '',
                };
                const siiKey = Object.keys(prev).find(k => k.trim().toLowerCase() === 'sii id');
                if (siiKey) {
                    next[siiKey] = '';
                } else {
                    next['SII ID'] = '';
                    next['sii id'] = '';
                }
                return next;
            });
            return;
        }

        const selectedCourseObj = coursesList.find(c => c.CourseCode === courseCode);
        const courseName = selectedCourseObj ? selectedCourseObj.CourseName : courseCode;

        setFormData(prev => ({
            ...prev,
            courseCode: courseCode,
            programName: courseName
        }));

        setIsFetchingCourseDetails(true);
        try {
            const detailsRes = await getMasterCourseDetailsByCourseCode(courseCode);
            if (detailsRes && detailsRes.success && detailsRes.data) {
                const details = detailsRes.data;
                setFormData(prev => {
                    const next = {
                        ...prev,
                        startDate: details.StartDate ? details.StartDate.split('T')[0] : '',
                        expectedEndDate: details.EndDate ? details.EndDate.split('T')[0] : '',
                        durationOfProgram: details.Duration ? String(details.Duration) : '',
                        semesters: details.Semesters !== undefined ? String(details.Semesters) : '',
                        annualCourseFee: details.AnnualCourseFee !== undefined ? String(details.AnnualCourseFee) : '',
                        scholarship: details.Scholarship !== undefined ? String(details.Scholarship) : '',
                        feeAfterScholarship: details.FeeAfterScholarship !== undefined ? String(details.FeeAfterScholarship) : '',
                        bankName: details.BankName || '',
                        bankBranch: details.BranchName || '',
                        bankAccountName: details.AccountName || '',
                        bankAccountNumber: details.AccountNumber ? String(details.AccountNumber) : '',
                        bankSwiftCode: details.SwiftCode || '',
                        bankIfscCode: details.IFSCCode || '',
                        bankMicrCode: details.MICRCode ? String(details.MICRCode) : '',
                    };

                    const bankSiiId = details.BankSIIId || '';
                    next.applicantSiiId = bankSiiId;

                    const siiKey = Object.keys(prev).find(k => k.trim().toLowerCase() === 'sii id');
                    if (siiKey) {
                        next[siiKey] = bankSiiId;
                    } else {
                        next['SII ID'] = bankSiiId;
                        next['sii id'] = bankSiiId;
                    }

                    return next;
                });
            }
        } catch (err) {
            console.error("Error fetching course details:", err);
        } finally {
            setIsFetchingCourseDetails(false);
        }
    };

    /**
     * Captures the rendered OfferLetterPDFTemplate DOM node as a fully self-contained
     * HTML string for server-side PDF conversion.
     *
     * Strategy:
     *   1. Clone the live DOM so the original is never mutated.
     *   2. Walk every element in the clone and apply the *computed* CSS values for
     *      all visually significant properties as inline `style` attributes. This
     *      bakes in every Tailwind utility class (including arbitrary values like
     *      `text-[#003366]`) as plain CSS — no runtime needed on the server.
     *   3. Fetch every <img> src and replace it with a base64 data URL so logos
     *      and watermarks are embedded and not dependent on localhost being reachable
     *      from the backend server.
     *   4. Strip all `class` attributes (styles are now fully inlined).
     */
    const buildGeneratedHtml = async () => {
        if (!templateRef.current) return null;

        // ── 1. Clone so we never touch the live template ───────────────────────
        const clone = templateRef.current.cloneNode(true);

        // ── 1b. Strip the outer wrapper's flex layout, gap and padding ──────────
        // The live wrapper uses `flex flex-col gap-8 p-8` (32px gaps + 32px padding).
        // In a continuous PDF render these gaps become blank pages between content.
        // Switching to `display: block` + zero spacing collapses them cleanly.
        clone.style.cssText = 'display: block; width: 820px; padding: 0; margin: 0; gap: 0; background: white;';

        // ── 1c. Mark page boundaries so SelectPdf breaks at the right spots ─────
        // Each direct child is one 820x1160px page div. Adding page-break-after
        // tells SelectPdf exactly where to cut — no guessing from content height.
        const pageChildren = Array.from(clone.children);
        pageChildren.forEach((pageDiv, idx) => {
            const existingStyle = pageDiv.getAttribute('style') || '';
            const breakStyle = idx < pageChildren.length - 1
                ? 'page-break-after: always; break-after: page; margin: 0;'
                : 'page-break-after: avoid; break-after: avoid; margin: 0;';
            pageDiv.setAttribute('style', existingStyle + '; ' + breakStyle);
        });

        // ── 2. Inline computed styles ───────────────────────────────────────────
        // Only the properties that actually affect visual output in a PDF.
        // Keeping this list targeted avoids generating multi-MB HTML payloads.
        const VISUAL_PROPS = [
            'color', 'background-color', 'background',
            'font-size', 'font-weight', 'font-family', 'font-style', 'font-variant',
            'text-decoration', 'text-align', 'text-transform', 'text-indent',
            'line-height', 'letter-spacing', 'white-space', 'word-break',
            'display', 'flex-direction', 'flex-wrap',
            'align-items', 'align-self', 'justify-content', 'justify-self',
            'flex', 'flex-grow', 'flex-shrink', 'flex-basis',
            'gap', 'column-gap', 'row-gap',
            'grid-template-columns', 'grid-column',
            'width', 'height', 'max-width', 'min-width', 'max-height', 'min-height',
            'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
            'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right',
            'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
            'border-color', 'border-style', 'border-width', 'border-radius',
            'border-top-color', 'border-bottom-color',
            'position', 'top', 'right', 'bottom', 'left',
            'z-index', 'opacity', 'overflow', 'overflow-x', 'overflow-y',
            'vertical-align', 'box-sizing',
            'object-fit', 'object-position',
            'list-style-type', 'list-style-position',
            'transform', 'transform-origin', 'pointer-events',
        ];

        const liveEls   = Array.from(templateRef.current.querySelectorAll('*'));
        const cloneEls  = Array.from(clone.querySelectorAll('*'));

        liveEls.forEach((liveEl, idx) => {
            const computed  = window.getComputedStyle(liveEl);
            const existing  = cloneEls[idx].getAttribute('style') || '';
            const inlined   = VISUAL_PROPS
                .map(prop => {
                    const val = computed.getPropertyValue(prop);
                    return val && val !== 'auto' && val !== 'normal' && val !== 'none'
                        ? `${prop}: ${val}`
                        : '';
                })
                .filter(Boolean)
                .join('; ');

            // Merge existing inline styles (which take priority) with computed ones
            cloneEls[idx].setAttribute('style', inlined + (existing ? '; ' + existing : ''));
            // Remove class attributes — styles are now fully inlined
            cloneEls[idx].removeAttribute('class');
        });

        // ── 3. Convert local image src → base64 data URLs ──────────────────────
        const imgEls = Array.from(clone.querySelectorAll('img'));
        await Promise.all(imgEls.map(async (img) => {
            const src = img.getAttribute('src');
            if (!src || src.startsWith('data:')) return; // already base64
            try {
                const res   = await fetch(src);
                const blob  = await res.blob();
                await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        img.setAttribute('src', reader.result);
                        resolve();
                    };
                    reader.readAsDataURL(blob);
                });
            } catch (e) {
                console.warn('Could not inline image as base64:', src, e);
            }
        }));

        // ── 4. Wrap in a complete HTML document ─────────────────────────────────
        return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Offer Letter</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    body { margin: 0; padding: 0; background: white; }
    sup { font-size: 10px; vertical-align: super; }
    ul { list-style-type: disc; padding-left: 20px; }
    ol { list-style-type: decimal; padding-left: 20px; }
    a { color: inherit; text-decoration: underline; }
  </style>
</head>
<body>
${clone.outerHTML}
</body>
</html>`;
    };

    const handleViewPDF = async () => {
        // Open blank window immediately to bypass popup blocker
        let previewWindow = null;
        try {
            previewWindow = window.open('', '_blank');
            if (previewWindow) {
                previewWindow.document.write(`
                    <div style="font-family: system-ui, -apple-system, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; color: #1e293b; background-color: #f8fafc; margin: 0;">
                        <div style="text-align: center; padding: 24px; border-radius: 12px; background: white; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);">
                            <div style="border: 3px solid #e2e8f0; border-top: 3px solid #3b82f6; border-radius: 50%; width: 24px; height: 24px; animation: spin 1s linear infinite; margin: 0 auto 12px auto;"></div>
                            <p style="font-size: 15px; font-weight: 600; margin: 0;">Generating Offer Letter PDF...</p>
                            <p style="font-size: 13px; color: #64748b; margin: 4px 0 0 0;">Please wait while we render the high-fidelity layout from the server.</p>
                        </div>
                        <style>
                            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                        </style>
                    </div>
                `);
            }
        } catch (wErr) {
            console.warn('Failed to pre-open preview window:', wErr);
        }

        setIsGeneratingPreview(true);
        const toastId = toast.loading("Generating PDF preview...");

        try {
            const offerLetterFields = masterFields.map((field) => {
                const fieldName = field.FieldName;
                const val = formData[fieldName] || '';
                return {
                    masterFieldId: field.MasterFormFieldId,
                    value: String(val),
                };
            });

            const selectedCollege = collegesList.find(c => c.CollegeId === formData.collegeId || c.Id === formData.collegeId);
            const rawJsonObj = {
                ScholarshipPercentage: formData.scholarship || "0",
                collegeId: formData.collegeId || '',
                collegeName: selectedCollege?.CollegeName || selectedCollege?.Name || formData.collegeName || '',
                courseCode: formData.courseCode || '',
                programName: formData.programName || '',
                durationOfProgram: formData.durationOfProgram || '',
                startDate: formData.startDate || '',
                expectedEndDate: formData.expectedEndDate || '',
                semesters: formData.semesters || '',
                annualCourseFee: formData.annualCourseFee || '',
                feeAfterScholarship: formData.feeAfterScholarship || '',
                provisionalRegistrationFeeDate: formData.provisionalRegistrationFeeDate || '',
                bankName: formData.bankName || '',
                bankBranch: formData.bankBranch || '',
                bankAccountName: formData.bankAccountName || '',
                bankAccountNumber: formData.bankAccountNumber || '',
                bankSwiftCode: formData.bankSwiftCode || '',
                bankIfscCode: formData.bankIfscCode || '',
                bankMicrCode: formData.bankMicrCode || '',
            };

            const payload = {
                leadId: leadId,
                rawJson: JSON.stringify(rawJsonObj),
                offerLetterFields: offerLetterFields,
                generatedHtml: await buildGeneratedHtml(),
            };

            // If editing an existing record, include its ID so the backend runs UPDATE instead of INSERT
            if (intLeadsOfferLetterId) {
                payload.intLeadsOfferLetterId = intLeadsOfferLetterId;
            }

            const result = await generateOfferLetterForInternationalStudent(payload);

            if (result.success && result.data) {
                // Parse the returned saved record
                let apiDataObj = result.data;
                if (typeof apiDataObj === 'string') {
                    try {
                        apiDataObj = JSON.parse(apiDataObj);
                    } catch (e) {
                        console.error('Failed to parse apiData JSON:', e);
                    }
                }

                // Update the state so the PDF template renders with backend data
                setApiData(apiDataObj);

                // Track the offer letter ID returned by the backend for future edits
                if (apiDataObj?.Id) {
                    setIntLeadsOfferLetterId(apiDataObj.Id);
                }

                // Wait for the component to render with the new apiData
                await new Promise((resolve) => setTimeout(resolve, 300));

                // ── Re-capture HTML now that apiData has the correct DisplayLeadId ──
                // The first call sent HTML with the fallback display ID because apiData
                // wasn't populated yet. Now that the template has re-rendered with the
                // real DisplayLeadId, re-capture and silently update the stored PDF.
                try {
                    const correctedHtml = await buildGeneratedHtml();
                    if (correctedHtml && apiDataObj?.Id) {
                        await generateOfferLetterForInternationalStudent({
                            leadId: leadId,
                            rawJson: JSON.stringify(rawJsonObj),
                            offerLetterFields: offerLetterFields,
                            generatedHtml: correctedHtml,
                            intLeadsOfferLetterId: apiDataObj.Id,
                        });
                    }
                } catch (updateErr) {
                    // Non-fatal: preview and form data are already saved correctly
                    console.warn('Could not update stored PDF with corrected Ref No:', updateErr);
                }

                if (!templateRef.current) {
                    toast.error("Failed to render preview template", { id: toastId });
                    if (previewWindow) previewWindow.close();
                    setIsGeneratingPreview(false);
                    return;
                }

                const pages = templateRef.current.children;
                if (!pages || pages.length === 0) {
                    toast.error("Failed to locate preview pages", { id: toastId });
                    if (previewWindow) previewWindow.close();
                    setIsGeneratingPreview(false);
                    return;
                }

                const pdf = new jsPDF({
                    orientation: 'portrait',
                    unit: 'px',
                    format: 'a4',
                });

                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();


                for (let i = 0; i < pages.length; i++) {
                    const pageEl = pages[i];

                    // Yield to the browser between pages so the UI stays responsive
                    await new Promise((r) => setTimeout(r, 0));

                    const canvas = await html2canvasPro(pageEl, {
                        scale: 1.5,          // 1.5 × native pixels → ~150 DPI on A4, crisp text & logos
                        useCORS: true,
                        logging: false,
                        backgroundColor: '#ffffff',
                    });

                    // JPEG at 92 % quality is visually identical to PNG for document content
                    // but produces files 8–10× smaller (PNG was ~43 MB for 4 pages).
                    const imgData = canvas.toDataURL('image/jpeg', 0.92);

                    if (i > 0) {
                        pdf.addPage();
                    }

                    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
                }


                const pdfBlob = pdf.output('blob');
                const fileURL = URL.createObjectURL(pdfBlob);

                // Trigger direct download of the PDF
                const fileName = `OfferLetter_${(formData.applicantName || 'Student').trim().replace(/\s+/g, '_')}.pdf`;
                pdf.save(fileName);

                if (previewWindow) {
                    previewWindow.location.href = fileURL;
                    toast.success("Offer letter PDF preview opened and downloaded successfully", { id: toastId });
                } else {
                    toast.success("Offer letter PDF downloaded successfully (preview popup blocked)", { id: toastId });
                }

                // Refresh lead data in the background so OfferLetterTab updates immediately
                onSuccess();

            } else {
                if (previewWindow) previewWindow.close();
                toast.error(result.error || result.message || "Failed to generate PDF preview", { id: toastId });
            }
        } catch (error) {
            console.error('Error generating preview PDF:', error);
            toast.error("Failed to generate PDF preview", { id: toastId });
            if (previewWindow) previewWindow.close();
        } finally {
            setIsGeneratingPreview(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setIsSubmitting(true);
        try {
            const offerLetterFields = masterFields.map((field) => {
                const fieldName = field.FieldName;
                const val = formData[fieldName] || '';
                return {
                    masterFieldId: field.MasterFormFieldId,
                    value: String(val),
                };
            });

            const selectedCollege = collegesList.find(c => c.CollegeId === formData.collegeId || c.Id === formData.collegeId);
            const rawJsonObj = {
                ScholarshipPercentage: formData.scholarship || "0",
                collegeId: formData.collegeId || '',
                collegeName: selectedCollege?.CollegeName || selectedCollege?.Name || formData.collegeName || '',
                courseCode: formData.courseCode || '',
                programName: formData.programName || '',
                durationOfProgram: formData.durationOfProgram || '',
                startDate: formData.startDate || '',
                expectedEndDate: formData.expectedEndDate || '',
                semesters: formData.semesters || '',
                annualCourseFee: formData.annualCourseFee || '',
                feeAfterScholarship: formData.feeAfterScholarship || '',
                provisionalRegistrationFeeDate: formData.provisionalRegistrationFeeDate || '',
                bankName: formData.bankName || '',
                bankBranch: formData.bankBranch || '',
                bankAccountName: formData.bankAccountName || '',
                bankAccountNumber: formData.bankAccountNumber || '',
                bankSwiftCode: formData.bankSwiftCode || '',
                bankIfscCode: formData.bankIfscCode || '',
                bankMicrCode: formData.bankMicrCode || '',
            };

            const payload = {
                leadId: leadId,
                rawJson: JSON.stringify(rawJsonObj),
                offerLetterFields: offerLetterFields,
                generatedHtml: await buildGeneratedHtml(),
            };

            // If we have an existing ID tracked, use it to update
            if (intLeadsOfferLetterId) {
                payload.intLeadsOfferLetterId = intLeadsOfferLetterId;
            }

            const result = await generateOfferLetterForInternationalStudent(payload);

            if (result.success || result.statusCode === 200) {
                toast.success(result.message || (mode === 'edit' ? 'Offer letter updated successfully!' : 'Offer letter generated successfully!'));
                onSuccess();
                handleClose();
            } else {
                toast.error(result.message || 'Failed to save offer letter');
            }
        } catch (error) {
            console.error('Error saving offer letter:', error);
            toast.error(error.response?.data?.message || 'Failed to save offer letter');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (isSubmitting || isGeneratingPreview) return;
        setFormData({
            applicantName: leadData?.name || '',
            applicantGender: '',
            fatherGuardianName: '',
            dateOfBirth: '',
            applicantCountry: '',
            applicantPassportNo: '',
            applicantSiiId: '',
            applicantEmail: leadData?.email || '',
            fatherEmail: '',
            fatherMobileNumber: '',
            guardianEmail: '',
            guardianMobileNumber: '',
            collegeId: '',
            courseCode: '',
            programName: '',
            durationOfProgram: '',
            startDate: '',
            expectedEndDate: '',
            semesters: '',
            annualCourseFee: '',
            scholarship: '',
            feeAfterScholarship: '',
            provisionalRegistrationFeeDate: '',
            bankName: '',
            bankBranch: '',
            bankAccountName: '',
            bankAccountNumber: '',
            bankSwiftCode: '',
            bankIfscCode: '',
            bankMicrCode: '',
        });
        setApiData(null);
        setMasterFields([]);
        setCoursesList([]);
        setIntLeadsOfferLetterId(null);
        onClose();
    };

    // Filter master fields (only keep the Country field that has Options to avoid empty dropdown placeholder inputs)
    const filteredFields = masterFields.filter((field) => {
        const isCountry = field.FieldName?.trim().toLowerCase() === 'country';
        if (isCountry) {
            const hasOptions = Array.isArray(field.Options) && field.Options.length > 0;
            return hasOptions;
        }
        return true;
    });

    // Partition master fields into Applicant and Parent/Guardian Details sections
    const applicantFields = [];
    const guardianFields = [];

    filteredFields.forEach((field) => {
        const label = (field.FieldLabel || '').toLowerCase();
        const name = (field.FieldName || '').toLowerCase();
        
        const isGuardianOrParent = 
            label.includes('father') || 
            label.includes('guardian') || 
            label.includes('emergency') || 
            name.includes('father') || 
            name.includes('guardian') || 
            name.includes('emergency') ||
            label === 'email address' ||
            name === 'email address';
            
        if (isGuardianOrParent) {
            guardianFields.push(field);
        } else {
            applicantFields.push(field);
        }
    });

    // Sort applicant fields to enforce specific display order:
    // 1. First Name, 2. Last Name, 3. SII ID, 4. Email, 5. Country, 6. Date of Birth, 7. Gender, 8. Passport Details
    applicantFields.sort((a, b) => {
        const getRank = (field) => {
            const label = (field.FieldLabel || '').toLowerCase().trim();
            const name = (field.FieldName || '').toLowerCase().trim();
            
            if (label.includes('first name') || name.includes('first_name') || label.includes('first_name')) return 1;
            if (label.includes('last name') || name.includes('last_name') || label.includes('last_name')) return 2;
            if (label.includes('sii') || name.includes('sii')) return 3;
            if (label === 'email' || name === 'email') return 4;
            if (label.includes('country') || name.includes('country')) return 5;
            if (label.includes('birth') || name.includes('birth') || label.includes('dob') || name.includes('dob')) return 6;
            if (label.includes('gender') || name.includes('gender')) return 7;
            if (label.includes('passport') || name.includes('passport')) return 8;
            return 100;
        };
        return getRank(a) - getRank(b);
    });

    // Sort parent/guardian fields to enforce specific display order:
    // 1. Father's Name, 2. Father's Contact Number, 3. Father's Email Address (Email Address), 4. Emergency Contact Details, 5. Guardian's Name
    guardianFields.sort((a, b) => {
        const getRank = (field) => {
            const label = (field.FieldLabel || '').toLowerCase().trim();
            const name = (field.FieldName || '').toLowerCase().trim();
            
            if (label.includes("father's name") || name.includes("father's name") || label.includes("father name") || name.includes("father name")) return 1;
            if (label.includes("contact number") || name.includes("contact number") || label.includes("father's contact") || name.includes("father's contact")) return 2;
            if (label.includes("email address") || name.includes("email address") || label.includes("father's email") || name.includes("father's email")) return 3;
            if (label.includes("emergency") || name.includes("emergency")) return 4;
            if (label.includes("guardian") || name.includes("guardian")) return 5;
            return 100;
        };
        return getRank(a) - getRank(b);
    });

    const collegeDropdownOptions = useMemo(() => {
        return collegesList.map(c => ({
            id: c.Id,
            label: c.CollegeName
        }));
    }, [collegesList]);

    const courseDropdownOptions = useMemo(() => {
        return coursesList.map(c => ({
            id: c.CourseCode,
            label: c.CourseName
        }));
    }, [coursesList]);

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" onClose={handleClose} className="fixed inset-0 z-[130] overflow-y-auto">
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                </TransitionChild>

                {/* Hidden layout for PDF generation */}
                <div
                    style={{
                        position: 'fixed',
                        left: '-9999px',
                        top: '0',
                        transform: 'none',
                        opacity: 1,
                        visibility: 'visible',
                        zIndex: -9999
                    }}
                >
                    <OfferLetterPDFTemplate
                        ref={templateRef}
                        formData={formData}
                        leadData={leadData}
                        apiData={apiData}
                    />
                </div>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel className="w-full max-w-3xl transform overflow-hidden rounded-lg bg-white dark:bg-dark-700 shadow-xl transition-all">
                                {/* Header */}
                                <div className="flex items-center justify-between border-b border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-800 px-6 py-4">
                                    <DialogTitle as="h3" className="text-lg font-semibold text-gray-900 dark:text-dark-50">
                                        {mode === 'edit' ? 'Edit Offer Letter' : 'Generate Offer Letter'}
                                    </DialogTitle>
                                    <Button
                                        onClick={handleClose}
                                        variant="flat"
                                        isIcon
                                        className="size-8 rounded-full"
                                        disabled={isSubmitting || isGeneratingPreview}
                                    >
                                        <XMarkIcon className="size-5" />
                                    </Button>
                                </div>

                                {/* Form */}
                                <form onSubmit={handleSubmit}>
                                    <div className="px-6 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
                                        <p className="text-sm text-gray-600 dark:text-gray-300">
                                            Fill in the details to generate an offer letter for {leadData?.name || 'this applicant'}.
                                        </p>

                                        {/* Applicant Details */}
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 pb-1 border-b border-gray-100 dark:border-dark-600">
                                                Applicant Details
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* <Input
                                                    label="Applicant's Name"
                                                    value={formData.applicantName}
                                                    onChange={(e) => handleInputChange('applicantName', e.target.value)}
                                                    disabled={isSubmitting}
                                                    required={false}
                                                    placeholder="Enter applicant's name"
                                                /> */}
                                                {applicantFields.map((field) => {
                                                    const fieldName = field.FieldName;
                                                    const fieldLabel = field.FieldLabel || fieldName;
                                                    const hasOptions = Array.isArray(field.Options) && field.Options.length > 0;
                                                    const value = formData[fieldName] || '';

                                                    if (hasOptions) {
                                                        return (
                                                            <FilterDropdown
                                                                key={field.MasterFormFieldId || fieldName}
                                                                filterKey={fieldName}
                                                                options={field.Options.map((opt) => ({
                                                                    id: opt.OptionValue,
                                                                    label: opt.OptionLabel,
                                                                }))}
                                                                selectedValue={value}
                                                                onSelect={(key, val) => handleInputChange(key, val)}
                                                                onClear={(key) => handleInputChange(key, '')}
                                                                label={fieldLabel}
                                                                placeholder={`Select ${fieldLabel.trim()}`}
                                                                showAllOption={false}
                                                                showSearch={true}
                                                                searchThreshold={0}
                                                                disabled={isSubmitting}
                                                                highlightSelected={false}
                                                            />
                                                        );
                                                    } else {
                                                        const lower = fieldName.trim().toLowerCase();
                                                        const isDateField = lower.includes('date') || lower.includes('birth') || lower.includes('dob');
                                                        
                                                        if (isDateField) {
                                                            return (
                                                                <PopoverDatePicker
                                                                    key={field.MasterFormFieldId || fieldName}
                                                                    label={fieldLabel}
                                                                    value={value}
                                                                    onChange={(val) => handleInputChange(fieldName, val)}
                                                                    disabled={isSubmitting}
                                                                    placeholder={field.Placeholder || `Select ${fieldLabel.trim()}`}
                                                                    className="w-full"
                                                                />
                                                            );
                                                        }

                                                        let inputType = 'text';
                                                        if (lower === 'email') {
                                                            inputType = 'email';
                                                        }

                                                        return (
                                                            <Input
                                                                key={field.MasterFormFieldId || fieldName}
                                                                label={fieldLabel}
                                                                type={inputType}
                                                                value={value}
                                                                onChange={(e) => handleInputChange(fieldName, e.target.value)}
                                                                disabled={isSubmitting}
                                                                required={false}
                                                                placeholder={field.Placeholder || `Enter ${fieldLabel.trim()}`}
                                                            />
                                                        );
                                                    }
                                                })}
                                            </div>
                                        </div>

                                        {/* Parent / Guardian Details */}
                                        {guardianFields.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 pb-1 border-b border-gray-100 dark:border-dark-600">
                                                    Parent / Guardian Details
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {guardianFields.map((field) => {
                                                        const fieldName = field.FieldName;
                                                        const fieldLabel = field.FieldLabel || fieldName;
                                                        const hasOptions = Array.isArray(field.Options) && field.Options.length > 0;
                                                        const value = formData[fieldName] || '';

                                                        if (hasOptions) {
                                                            return (
                                                                <FilterDropdown
                                                                    key={field.MasterFormFieldId || fieldName}
                                                                    filterKey={fieldName}
                                                                    options={field.Options.map((opt) => ({
                                                                        id: opt.OptionValue,
                                                                        label: opt.OptionLabel,
                                                                    }))}
                                                                    selectedValue={value}
                                                                    onSelect={(key, val) => handleInputChange(key, val)}
                                                                    onClear={(key) => handleInputChange(key, '')}
                                                                    label={fieldLabel}
                                                                    placeholder={`Select ${fieldLabel.trim()}`}
                                                                    showAllOption={false}
                                                                    showSearch={true}
                                                                    searchThreshold={0}
                                                                    disabled={isSubmitting}
                                                                    highlightSelected={false}
                                                                />
                                                            );
                                                        } else {
                                                            const lower = fieldName.trim().toLowerCase();
                                                            const isDateField = lower.includes('date') || lower.includes('birth') || lower.includes('dob');

                                                            if (isDateField) {
                                                                return (
                                                                    <PopoverDatePicker
                                                                        key={field.MasterFormFieldId || fieldName}
                                                                        label={fieldLabel}
                                                                        value={value}
                                                                        onChange={(val) => handleInputChange(fieldName, val)}
                                                                        disabled={isSubmitting}
                                                                        placeholder={field.Placeholder || `Select ${fieldLabel.trim()}`}
                                                                        className="w-full"
                                                                    />
                                                                );
                                                            }

                                                            let inputType = 'text';
                                                            const isPhoneField = lower.includes('phone') || lower.includes('mobile') || lower.includes('contact') || lower.includes('fax') || lower.includes('tel');
                                                            if (lower.includes('email')) {
                                                                inputType = 'email';
                                                            } else if (isPhoneField) {
                                                                inputType = 'tel';
                                                            }

                                                            return (
                                                                <Input
                                                                    key={field.MasterFormFieldId || fieldName}
                                                                    label={fieldLabel}
                                                                    type={inputType}
                                                                    value={value}
                                                                    onChange={(e) => {
                                                                        const raw = e.target.value;
                                                                        // For phone fields, strip everything that isn't a digit
                                                                        const cleaned = isPhoneField ? raw.replace(/\D/g, '') : raw;
                                                                        handleInputChange(fieldName, cleaned);
                                                                    }}
                                                                    disabled={isSubmitting}
                                                                    required={false}
                                                                    placeholder={field.Placeholder || `Enter ${fieldLabel.trim()}`}
                                                                />
                                                            );
                                                        }
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Program Details */}
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 pb-1 border-b border-gray-100 dark:border-dark-600">
                                                Program Details
                                            </h4>
                                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <FilterDropdown
                                                    filterKey="collegeId"
                                                    options={collegeDropdownOptions}
                                                    selectedValue={formData.collegeId}
                                                    onSelect={(key, val) => handleCollegeChange(val)}
                                                    onClear={() => handleCollegeChange('')}
                                                    label="College"
                                                    placeholder="Select College..."
                                                    showAllOption={false}
                                                    showSearch={true}
                                                    searchThreshold={0}
                                                    disabled={isSubmitting}
                                                    highlightSelected={false}
                                                />
                                                <FilterDropdown
                                                    filterKey="courseCode"
                                                    options={courseDropdownOptions}
                                                    selectedValue={formData.courseCode}
                                                    onSelect={(key, val) => handleCourseChange(val)}
                                                    onClear={() => handleCourseChange('')}
                                                    label={
                                                        <div className="flex items-center gap-1">
                                                            <span>Course</span>
                                                            {isLoadingCourses && (
                                                                <svg className="animate-spin h-3.5 w-3.5 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                            )}
                                                        </div>
                                                    }
                                                    placeholder={formData.collegeId ? "Choose a course..." : "Select college first"}
                                                    showAllOption={false}
                                                    showSearch={true}
                                                    searchThreshold={0}
                                                    disabled={isSubmitting || !formData.collegeId || isLoadingCourses}
                                                    highlightSelected={false}
                                                />
                                                <PopoverDatePicker
                                                    label="Start Date"
                                                    value={formData.startDate}
                                                    onChange={(val) => handleInputChange('startDate', val)}
                                                    disabled={isSubmitting || isFetchingCourseDetails}
                                                    placeholder="Select Start Date"
                                                    className="w-full"
                                                />
                                                <PopoverDatePicker
                                                    label="Expected End Date"
                                                    value={formData.expectedEndDate}
                                                    onChange={(val) => handleInputChange('expectedEndDate', val)}
                                                    disabled={isSubmitting || isFetchingCourseDetails}
                                                    placeholder="Select Expected End Date"
                                                    className="w-full"
                                                />
                                                <Input
                                                    label="Duration of Program"
                                                    value={formData.durationOfProgram}
                                                    onChange={(e) => handleInputChange('durationOfProgram', e.target.value)}
                                                    disabled={isSubmitting || isFetchingCourseDetails}
                                                    required={false}
                                                    placeholder="e.g., 3 Years"
                                                />
                                                <Input
                                                    label="Semesters"
                                                    value={formData.semesters}
                                                    onChange={(e) => handleInputChange('semesters', e.target.value)}
                                                    disabled={isSubmitting || isFetchingCourseDetails}
                                                    required={false}
                                                    placeholder="e.g., 6"
                                                />
                                                <Input
                                                    label="Annual Course Fee"
                                                    value={formData.annualCourseFee}
                                                    onChange={(e) => handleInputChange('annualCourseFee', e.target.value)}
                                                    disabled={isSubmitting || isFetchingCourseDetails}
                                                    required={false}
                                                    placeholder="e.g., 3350.00"
                                                />
                                                <Input
                                                    label="Scholarship (%)"
                                                    value={formData.scholarship}
                                                    onChange={(e) => handleInputChange('scholarship', e.target.value)}
                                                    disabled={isSubmitting || isFetchingCourseDetails}
                                                    required={false}
                                                    placeholder="e.g., 55.00"
                                                />
                                                <Input
                                                    label="Fee After Scholarship"
                                                    value={formData.feeAfterScholarship}
                                                    onChange={(e) => handleInputChange('feeAfterScholarship', e.target.value)}
                                                    disabled={isSubmitting || isFetchingCourseDetails}
                                                    required={false}
                                                    placeholder="e.g., 1500.00"
                                                />
                                                <PopoverDatePicker
                                                    label="Provisional Registration Fee Date"
                                                    value={formData.provisionalRegistrationFeeDate}
                                                    onChange={(val) => handleInputChange('provisionalRegistrationFeeDate', val)}
                                                    disabled={isSubmitting}
                                                    placeholder="Select deadline date"
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>

                                        {/* Beneficiary Bank Details (Read-only reference) */}
                                        {formData.bankName && (
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 pb-1 border-b border-gray-100 dark:border-dark-600">
                                                    Beneficiary Bank Details (Auto-populated from Course)
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 dark:bg-dark-800/40 p-4 rounded-lg border border-gray-200 dark:border-dark-600">
                                                    <Input
                                                        label="Bank Name"
                                                        value={formData.bankName}
                                                        disabled={true}
                                                        readOnly={true}
                                                    />
                                                    <Input
                                                        label="Branch / Address"
                                                        value={formData.bankBranch}
                                                        disabled={true}
                                                        readOnly={true}
                                                    />
                                                    <Input
                                                        label="Account Name"
                                                        value={formData.bankAccountName}
                                                        disabled={true}
                                                        readOnly={true}
                                                    />
                                                    <Input
                                                        label="Account Number"
                                                        value={formData.bankAccountNumber}
                                                        disabled={true}
                                                        readOnly={true}
                                                    />
                                                    <Input
                                                        label="SWIFT Code"
                                                        value={formData.bankSwiftCode}
                                                        disabled={true}
                                                        readOnly={true}
                                                    />
                                                    <Input
                                                        label="IFSC Code"
                                                        value={formData.bankIfscCode}
                                                        disabled={true}
                                                        readOnly={true}
                                                    />
                                                    <Input
                                                        label="MICR Code"
                                                        value={formData.bankMicrCode}
                                                        disabled={true}
                                                        readOnly={true}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer */}
                                    <div className="border-t border-gray-200 dark:border-dark-600 px-6 py-4 bg-gray-50 dark:bg-dark-800 flex justify-end gap-3">
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            onClick={handleViewPDF}
                                            disabled={isSubmitting || isGeneratingPreview}
                                        >
                                            {isGeneratingPreview
                                                ? 'Generating...'
                                                : mode === 'edit'
                                                    ? 'Update & Generate'
                                                    : 'Generate'
                                            }
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            onClick={handleClose}
                                            disabled={isSubmitting || isGeneratingPreview}
                                        >
                                            Cancel
                                        </Button>
                                        {/* <Button
                                            type="submit"
                                            variant="solid"
                                            color="primary"
                                            disabled={isSubmitting || isGeneratingPreview}
                                        >
                                            {isSubmitting
                                                ? (mode === 'edit' ? 'Updating...' : 'Generating...')
                                                : (mode === 'edit' ? 'Update Offer Letter' : 'Generate Offer Letter')
                                            }
                                        </Button> */}
                                    </div>
                                </form>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default GenerateOfferLetterModal;
